import * as moment from 'moment';

type successResponse<T> = {
    status: 'success',
    msg?: string,
    data: T
}

type errorResponse<T> = {
    status: 'error',
    msg?: string
}

export type response<T> = successResponse<T> | errorResponse<T>;

export function postJson<T = response<never>>(url: string, body: object, init?: RequestInit, includeVerification: boolean = true) {

    const postInit: RequestInit = { method: 'post', body: JSON.stringify(body), credentials: 'same-origin' };
    init = init ? { ...postInit, ...init } : postInit;

    if (includeVerification) {
        const {
            headers,
        } = init;

        if (headers) {
            init.headers = new Headers(headers);
        } else {
            init.headers = new Headers();
        }

        init.headers.append('content-type', 'application/json');
        init.headers.append('x-RequestVerificationToken', (document.getElementsByName('__RequestVerificationToken')[0] as HTMLInputElement).value);
    }

    function handleResponse(response: Response) {
        function handleJsonText(text: string): T  {
            if (text) {
                try {
                    var json = JSON.parse(text);
                } catch (e) {
                    if (response.ok) {
                        throw new Error("Error parsing JSON: " + e + "Response: " + text);
                    } else {
                        throw new Error("Error(" + response.status + "): " + (text || response.statusText));
                    }
                }

                if (response.ok) return json;
                throw json;
            }
            
            if (!response.ok) {
                throw new Error("Error(" + response.status + "): " + (response.statusText));
            }

            return null as any as T; // Small type hack to NOT have to include null in the signature, forcing checks on all API calls for server failure.
        }

        return response.text().then(handleJsonText);
    }

    return fetch(url, init).then(handleResponse);
}

export function fetchJson<T>(input: string | Request, init?: RequestInit) {

    const getHeaders = { 'accept': 'application/json' }
    const getInit: RequestInit = { method: 'get', credentials: 'same-origin', headers: getHeaders };

    if (init) {
        let headers = init.headers ? { ...getHeaders, ...init.headers } : getHeaders;
        init = { ...getInit, ...init, headers }
    } else {
        init = getInit;
    }

    function handleResponse(response: Response) {
        function handleJsonText(text: string): T {
            if (text) {
                try {
                    var json = JSON.parse(text);
                } catch (e) {
                    if (response.ok) {
                        throw new Error("Error parsing JSON: " + e + "Response: " + text);
                    } else {
                        throw new Error("Error(" + response.status + "): " + (text || response.statusText));
                    }
                }

                if (response.ok) return json;
                throw json;
            }

            if (!response.ok) {
                throw new Error("Error(" + response.status + "): " + (response.statusText));
            }

            return null as any as T; // Small type hack to NOT have to include null in the signature, forcing checks on all API calls for server failure.
        }

        return response.text().then(handleJsonText);
    }

    return fetch(input, init).then(handleResponse)
}

export function verifyNotNull<T>(data: Promise<T | null>) {
    return data.then(v => {
        if (v === null)
            throw new Error("Received empty result");

        return v;
    })
}

type MappedTypeSource = { [P in string]?: string }
type ValueOf<T> = T[keyof T];
type KeyValueTupleToObject<T extends [keyof any | undefined, any]> = { [K in Extract<T[0], string>]: Extract<T, [K, any]>[1] }
type KeyValueTupleFromObject<T extends MappedTypeSource, P> = ValueOf<{ [K in keyof T]: [T[K], P] }>;
type ObjectFromValues<T extends MappedTypeSource, P> = KeyValueTupleToObject<KeyValueTupleFromObject<T, P>>

type ModelDateMapBase<TSource> = { [P in keyof TSource]?: DateFieldMapBase }

type DateFieldMapBase = {
    date?: string;
    time?: string;
    datetime?: string;
}

type ModelDateMap<TSource, TDestination, TMap extends { [P in keyof TSource]?: DateFieldMap<TSource, TDestination> }> = {
    [P in keyof TMap]: P extends keyof TSource ? DateFieldMap<TSource, TDestination> : never
}

type DateFieldMap<TSource, TDestination> = {
    date?: keyof Subtract<TDestination, TSource>;
    time?: keyof Subtract<TDestination, TSource>;
    datetime?: keyof Subtract<TDestination, TSource>;
}

export type WithDates<TSource extends { [P in keyof TMap]?: string}, TMap extends ModelDateMapBase<TMap>> = Omit<TSource, keyof TMap> & { [P in keyof TMap]: TSource[P] extends string ? moment.Moment : (moment.Moment | null) };

export function getDates<T extends { [P in keyof K]?: string }, K extends ModelDateMapBase<T>>(object: Readonly<T>, props: K): { [P in keyof K]: T[P] extends string ? moment.Moment : (moment.Moment | null) } {
    type resultType<P extends keyof K> = T[P] extends string ? moment.Moment : moment.Moment | null;
    let result = {} as { [P in keyof K]: resultType<P> };
    for (var k in props) {
        result[k] = ((object[k] && moment.utc(object[k])) || null) as resultType<keyof K>;
    }
    return result;
}


//type dateFormat = 'MM/dd/yyyy' | 'MM-dd-yyyy' | 'M/d/yyyy' | 'yyyy-MM-dd' | 'yyyy-M-d' | 'yyyy/MM/dd' | 'yyyy/M/d' | 

// Date Map-- maps C# to jquery and moment formats (08/13/18 RKH: backlog #12359)
const dateMap = {

    'MM/dd/yyyy': {
        momentFormat: 'MM/DD/YYYY',
        jqueryFormat: 'mm/dd/yy'
    },
    'MM-dd-yyyy': {
        momentFormat: 'MM-DD-YYYY',
        jqueryFormat: 'mm-dd-yy'
    },

    'M/d/yyyy': {
        momentFormat: 'M/D/YYYY',
        jqueryFormat: 'm/d/yy'
    },
    'yyyy-MM-dd': {
        momentFormat: 'YYYY-MM-DD',
        jqueryFormat: 'yy-mm-dd'
    },
    'yyyy-M-d': {
        momentFormat: 'YYYY-M-D',
        jqueryFormat: 'yy-m-d'
    },
    'yyyy/MM/dd': {
        momentFormat: 'YYYY/MM/DD',
        jqueryFormat: 'yy/mm/dd'
    },

    'yyyy/M/d': {
        momentFormat: 'YYYY/M/D',
        jqueryFormat: 'yy/m/d'
    },
    'yyyy.MM.dd.': {
        momentFormat: 'YYYY.MM.DD.',
        jqueryFormat: 'yy.mm.dd.'
    },
    'yyyy.M.d': {
        momentFormat: 'YYYY.M.D',
        jqueryFormat: 'yy.m.d'
    },
    'yyyy.d.M': {
        momentFormat: 'YYYY.D.M',
        jqueryFormat: 'yy.d.m'
    },
    'yyyy. M. d': {
        momentFormat: 'YYYY. M. D',
        jqueryFormat: 'mm-dd-yy'
    },
    'yyyy?M?d?': {
        momentFormat: 'YYYY?M?D?',
        jqueryFormat: 'yy?m?d?'
    },
    'd.M.yyyy': {
        momentFormat: 'D.M.YYYY',
        jqueryFormat: 'd.m.yy'
    },
    'd.MM.yyyy': {
        momentFormat: 'D.MM.YYYY',
        jqueryFormat: 'd.mm.yy'
    },
    'd/M/yyyy': {
        momentFormat: 'D/M/YYYY',
        jqueryFormat: 'd/m/yy'
    },
    'd/MM/yyyy': {
        momentFormat: 'D/MM/YYYY',
        jqueryFormat: 'd/mm/yy'
    },
    'dd.MM.yyyy': {
        momentFormat: 'DD.MM.YYYY',
        jqueryFormat: 'dd.mm.yy'
    },
    'dd/MM/yyyy': {
        momentFormat: 'DD/MM/YYYY',
        jqueryFormat: 'dd/mm/yy'
    },
    'dd-MM-yyyy': {
        momentFormat: 'DD-MM-YYYY',
        jqueryFormat: 'dd-mm-yy'
    },
    'd-M-yyyy': {
        momentFormat: 'D-M-YYYY',
        jqueryFormat: 'd-m-yy'
    },
}
type dateFormat = keyof typeof dateMap;

//array of valid moment format's incl. times, used by formatDate function to declare valid formats so moment does not throw js date deprication warning (09/14/18 RKH: backlog #12359-- dynamic date formatting)
var momentFormatArray = [
    'MM/DD/YYYY',
    'MM/DD/YYYY HH:mm',
    'MM/DD/YYYY hh:mm a',
    'MM/DD/YYYY h:mm a',
    'MM/DD/YYYY hh:mm A',
    'MM/DD/YYYY h:mm A',

    'MM-DD-YYYY',
    'MM-DD-YYYY HH:mm',
    'MM-DD-YYYY hh:mm a',
    'MM-DD-YYYY h:mm a',
    'MM-DD-YYYY hh:mm A',
    'MM-DD-YYYY h:mm A',

    'M/D/YYYY',
    'M/D/YYYY HH:mm',
    'M/D/YYYY hh:mm a',
    'M/D/YYYY h:mm a',
    'M/D/YYYY hh:mm A',
    'M/D/YYYY h:mm A',

    'YYYY-MM-DD',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DD hh:mm a',
    'YYYY-MM-DD h:mm a',
    'YYYY-MM-DD hh:mm A',
    'YYYY-MM-DD h:mm A',

    'YYYY-M-D',
    'YYYY-M-D HH:mm',
    'YYYY-M-D hh:mm a',
    'YYYY-M-D h:mm a',
    'YYYY-M-D hh:mm A',
    'YYYY-M-D h:mm A',

    'YYYY/MM/DD',
    'YYYY/MM/DD HH:mm',
    'YYYY/MM/DD hh:mm a',
    'YYYY/MM/DD h:mm a',
    'YYYY/MM/DD hh:mm A',
    'YYYY/MM/DD h:mm A',

    'YYYY/M/D',
    'YYYY/M/D HH:mm',
    'YYYY/M/D hh:mm a',
    'YYYY/M/D h:mm a',
    'YYYY/M/D hh:mm A',
    'YYYY/M/D h:mm A',

    'YYYY.MM.DD.',
    'YYYY.MM.DD. HH:mm',
    'YYYY.MM.DD. hh:mm a',
    'YYYY.MM.DD. h:mm a',
    'YYYY.MM.DD. hh:mm A',
    'YYYY.MM.DD. h:mm A',

    'YYYY.M.D',
    'YYYY.M.D HH:mm',
    'YYYY.M.D hh:mm a',
    'YYYY.M.D h:mm a',
    'YYYY.M.D hh:mm A',
    'YYYY.M.D h:mm A',

    'YYYY.D.M',
    'YYYY.D.M HH:mm',
    'YYYY.D.M hh:mm a',
    'YYYY.D.M h:mm a',
    'YYYY.D.M hh:mm A',
    'YYYY.D.M h:mm A',

    'YYYY. M. D',
    'YYYY. M. D HH:mm',
    'YYYY. M. D hh:mm a',
    'YYYY. M. D h:mm a',
    'YYYY. M. D hh:mm A',
    'YYYY. M. D h:mm A',

    'YYYY?M?D?',
    'YYYY?M?D? HH:mm',
    'YYYY?M?D? hh:mm a',
    'YYYY?M?D? h:mm a',
    'YYYY?M?D? hh:mm A',
    'YYYY?M?D? h:mm A',

    'D.M.YYYY',
    'D.M.YYYY HH:mm',
    'D.M.YYYY hh:mm a',
    'D.M.YYYY h:mm a',
    'D.M.YYYY hh:mm A',
    'D.M.YYYY h:mm A',

    'D.MM.YYYY',
    'D.MM.YYYY HH:mm',
    'D.MM.YYYY hh:mm a',
    'D.MM.YYYY h:mm a',
    'D.MM.YYYY hh:mm A',
    'D.MM.YYYY h:mm A',

    'D/M/YYYY',
    'D/M/YYYY HH:mm',
    'D/M/YYYY hh:mm a',
    'D/M/YYYY h:mm a',
    'D/M/YYYY hh:mm A',
    'D/M/YYYY h:mm A',

    'D/MM/YYYY',
    'D/MM/YYYY HH:mm',
    'D/MM/YYYY hh:mm a',
    'D/MM/YYYY h:mm a',
    'D/MM/YYYY hh:mm A',
    'D/MM/YYYY h:mm A',

    'DD.MM.YYYY',
    'DD.MM.YYYY HH:mm',
    'DD.MM.YYYY hh:mm a',
    'DD.MM.YYYY h:mm a',
    'DD.MM.YYYY hh:mm A',
    'DD.MM.YYYY h:mm A',

    'DD/MM/YYYY',
    'DD/MM/YYYY HH:mm',
    'DD/MM/YYYY hh:mm a',
    'DD/MM/YYYY h:mm a',
    'DD/MM/YYYY hh:mm A',
    'DD/MM/YYYY h:mm A',

    'DD-MM-YYYY',
    'DD-MM-YYYY HH:mm',
    'DD-MM-YYYY hh:mm a',
    'DD-MM-YYYY h:mm a',
    'DD-MM-YYYY hh:mm A',
    'DD-MM-YYYY h:mm A',

    'D-M-YYYY',
    'D-M-YYYY HH:mm',
    'D-M-YYYY hh:mm a',
    'D-M-YYYY h:mm a',
    'D-M-YYYY hh:mm A',
    'D-M-YYYY h:mm A'
]

export enum FormatType {
    Date = 1,
    DateTime24 = 2,
    DateTime12 = 3,
    DateServer = 4,
    DateTime24Server = 5,
    DateTime12Server = 6,
}

//format a date to the user or server formats using moment (08/13/18 RKH: backlog #12359)
// type = 1 Date Only, 2 = Date and Time, 3 = Date Time and AmPm, 4 - Date Server Format 5 = Date Time Server Format, 6 = Date Time am/pm Server format, 7 = custom format
export function formatDate(value: string, formatType: FormatType, valueFormat: string |string[], dateFormat?: dateFormat): string {

    //trap empty value
    if (value === '')
        return '';

    //if input format unknown-- passing array of moment formats prevents moment from falling back to js date and throwing a deprication error
    valueFormat = valueFormat || momentFormatArray;
    dateFormat = dateFormat || 'MM/dd/yyyy';

    //trap value format not being passed in
    if (valueFormat === undefined) {
        valueFormat = "";
    }

    //format value based on type
    switch (formatType) {
        case FormatType.Date:
            return moment.default(value, valueFormat).format(dateMap[dateFormat].momentFormat);
        case FormatType.DateTime24:
            //input format known-- prevents moment deprication warning, moment does not fall back to js.date
            return moment.default(value, valueFormat).format(dateMap[dateFormat].momentFormat + " HH:mm");
        case FormatType.DateTime12:
            return moment.default(value, valueFormat).format(dateMap[dateFormat].momentFormat + " hh:mm a");
        case FormatType.DateServer:
            return moment.default(value, valueFormat).format('YYYY-MM-DD');
        case FormatType.DateTime24Server:
            return moment.default(value, valueFormat).format("YYYY-MM-DD HH:mm");
        case FormatType.DateTime12Server:
            return moment.default(value, valueFormat).format("YYYY-MM-DD hh:mm a");
        default:
            return "<Unknown date format>";
    }
}

const defaultDateStringMap: {
    time: 'time',
    date: 'date',
    datetime: 'datetime'
} = {
    time: 'time',
    date: 'date',
    datetime: 'datetime'
}

export function getDateStrings<TMap extends DateFieldMapBase = typeof defaultDateStringMap>(date: moment.Moment | null, map?: TMap): ObjectFromValues<TMap, string>;
export function getDateStrings<TData extends { [P in keyof TData]: moment.Moment | null }, TMap extends { [P in keyof TMap]: DateFieldMapBase }, K extends (keyof TData) & (keyof TMap)>(data: TData, map: TMap, key: K): ObjectFromValues<TMap[K], string>;
export function getDateStrings(data:any, map:any, key?:any) {

    var props;
    var date;

    if (key !== undefined) {
        props = map[key];
        date = data[key];
    } else {
        props = map || defaultDateStringMap;
        date = data;
    }

    var result = {} as { [key: string]: string | null };
    props.date && (result[props.date] = date ? formatDate(date, 1, '') : null); //(12/13/18 RKH: backlog #12359-- dynamic date formatting)
    props.time && (result[props.time] = date ? date.format('HH:mm') : null);
	props.datetime && (result[props.datetime] = date ? formatDate(date, 2, '') : null); //(12/13/18 RKH: backlog #12359-- dynamic date formatting)
    return result; 
} 

export function getOffsetFromParent(child: HTMLElement, parent: Element) {
    var result = { x: 0, y: 0 };
    while (child && child !== parent) {
        result.x += child.offsetLeft;
        result.y += child.offsetTop;
        child = child.offsetParent as HTMLElement;
    }

    return result;
}